/**
 * Dynamic Bundle Savings Calculator
 * Displays accurate savings badges for bundle products in all markets/currencies
 */


// =============================================================================
// CONFIGURATION - Update this with bundle product handles and their components
// =============================================================================
const BUNDLE_CONFIG = {
  // Format: 'bundle-product-handle': ['component-1-handle', 'component-2-handle', ...]
  
  'the-traditionalist-exclusive-set': [
    'uma-linen-knit-polo-top-oat-marle', 
    'uma-linen-knit-polo-top-noir'
  ],
  
  'the-tastemaker-exclusive-set': [
    'eden-organic-mini-dress-burgundy',
    'eden-organic-mini-dress-creme'
  ],
  
  // Add your other 3 bundles here:
  'the-makers-uniform-exclusive-set': [
    'dante-denim-long-sleeve-shirt-blue-denim',
    'dallas-denim-jeans-blue-denim'
  ],
  
  'the-sweet-minimalist-exclusive-set': [
    'frannie-silk-midi-skirt-vanilla-spot',
    'goldie-knit-vest-creme'
  ],
  
  'the-sunset-seeker-exclusive-set': [
    'carlotta-linen-wide-pants-brown-stripe',
    'ramona-linen-long-sleeve-shirt-brown-stripe',
    'kassius-linen-knit-tank-tangerine'
  ]
};

// =============================================================================
// MAIN CODE  
// =============================================================================

class BundleBadgeManager {
  constructor(config) {
    this.config = config;
    this.processedProducts = new Set();
    this.priceCache = new Map();
    
    // Pre-fetch all bundle and component data on init
    this.prefetchPromise = this.prefetchAllProducts();
    
    this.init();
  }

  async init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.start());
    } else {
      await this.start();
    }
  }

  async start() {
    // Reduced delay significantly
    await new Promise(resolve => setTimeout(resolve, 100));
    await this.processBundles();
    this.observeNewContent();
  }

  /**
   * Pre-fetch all bundle and component products in parallel
   * This eliminates sequential waiting
   */
  async prefetchAllProducts() {
    const allHandles = new Set();
    
    // Collect all handles
    Object.entries(this.config).forEach(([bundleHandle, componentHandles]) => {
      allHandles.add(bundleHandle);
      componentHandles.forEach(handle => allHandles.add(handle));
    });
    
    // Fetch all in parallel
    const fetchPromises = Array.from(allHandles).map(handle => 
      this.fetchProduct(handle)
    );
    
    await Promise.all(fetchPromises);
  }

  async processBundles() {
    // Wait for prefetch to complete
    await this.prefetchPromise;
    
    const selectors = [
      '[data-depict-product-id]',
      '.tile-product',
      '.product-card',
      '.grid__item:has(a[href*="/products/"])',
      'li:has(a[href*="/products/"])',
      '[class*="product"]:has(a[href*="/products/"])',
    ];
    
    let productCards = [];
    
    for (const selector of selectors) {
      try {
        const cards = document.querySelectorAll(selector);
        if (cards.length > 0) {
          productCards = Array.from(cards);
          break;
        }
      } catch (e) {
        continue;
      }
    }
    
    if (productCards.length === 0) {
      const productLinks = document.querySelectorAll('a[href*="/products/"]');
      const uniqueContainers = new Set();
      
      productLinks.forEach(link => {
        let container = link.closest('li, .grid__item, [class*="product"], [class*="card"], [class*="tile"]');
        if (container && !uniqueContainers.has(container)) {
          uniqueContainers.add(container);
          productCards.push(container);
        }
      });
    }
    
    // Process all bundles in parallel instead of sequentially
    const bundlePromises = [];
    
    for (const card of productCards) {
      const productLink = card.querySelector('a[href*="/products/"]');
      if (!productLink) continue;
      
      const handle = this.extractHandle(productLink.href);
      
      if (handle && this.config[handle]) {
        const productId = card.getAttribute('data-depict-product-id') || 
                         card.getAttribute('data-product-id') || 
                         handle;
        
        if (!this.processedProducts.has(productId)) {
          bundlePromises.push(this.processBundle(card, handle, productId));
          this.processedProducts.add(productId);
        }
      }
    }
    
    // Wait for all badges to be created
    await Promise.all(bundlePromises);
  }

  extractHandle(url) {
    const match = url.match(/\/products\/([^?#]+)/);
    return match ? match[1] : null;
  }

  async processBundle(card, bundleHandle, productId) {
    try {
      // All data should be cached now, so this is instant
      const bundleData = this.priceCache.get(bundleHandle);
      if (!bundleData) return;
      
      const bundlePrice = bundleData.price;
      const componentHandles = this.config[bundleHandle];
      
      // Get from cache (already fetched)
      const componentTotal = componentHandles.reduce((sum, handle) => {
        const product = this.priceCache.get(handle);
        return sum + (product ? product.price : 0);
      }, 0);
      
      if (componentTotal > bundlePrice) {
        const savings = componentTotal - bundlePrice;
        this.addBadge(card, savings);
      }
    } catch (error) {
      // Silently fail
    }
  }

  async fetchProduct(handle) {
    if (this.priceCache.has(handle)) {
      return this.priceCache.get(handle);
    }
    
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        this.priceCache.set(handle, null);
        return null;
      }
      
      const data = await response.json();
      this.priceCache.set(handle, data);
      return data;
    } catch (error) {
      this.priceCache.set(handle, null);
      return null;
    }
  }

  addBadge(card, savingsInCents) {
    const mediaWrapperSelectors = [
      '.tile-media-wrapper',
      '.product-card__image-wrapper',
      '.card__media',
      '.product__media',
      '[class*="media"]',
      '[class*="image"]'
    ];
    
    let mediaWrapper = null;
    for (const selector of mediaWrapperSelectors) {
      mediaWrapper = card.querySelector(selector);
      if (mediaWrapper) break;
    }
    
    if (!mediaWrapper) {
      const img = card.querySelector('img');
      if (img) mediaWrapper = img.closest('a, div');
    }
    
    if (!mediaWrapper) return;
    
    const computedStyle = window.getComputedStyle(mediaWrapper);
    if (computedStyle.position === 'static') {
      mediaWrapper.style.position = 'relative';
    }
    
    let container = mediaWrapper.querySelector('.bundle-badge-container');
    
    if (!container) {
      container = document.createElement('div');
      container.className = 'bundle-badge-container absolute top-1 left-1 pointer-events-none flex flex-wrap gap-1 z-10';
      container.style.cssText = 'position: absolute; top: 0.25rem; left: 0.25rem; z-index: 10; display: flex; flex-wrap: wrap; gap: 0.25rem; pointer-events: none;';
      mediaWrapper.appendChild(container);
    }

    const formatted = this.formatMoney(savingsInCents);
    
    const badge = document.createElement('span');
    badge.className = 'bundle-savings-badge rounded-button bg-scheme-accent text-scheme-accent-contrast px-2';
    badge.style.cssText = 'background-color: var(--color-accent, #000); color: var(--color-accent-text, #fff); padding: 0.25rem 0.5rem; border-radius: 0.25rem; font-size: 0.875rem; font-weight: 500; text-transform: uppercase; pointer-events: auto;';
    badge.textContent = `SAVE ${formatted}`;
    
    const old = container.querySelector('.bundle-savings-badge');
    if (old) old.remove();
    
    container.appendChild(badge);
  }

  formatMoney(cents) {
    let format = '${{amount_no_decimals}}';
    
    if (window.Shopify?.currency?.active?.money_format) {
      format = window.Shopify.currency.active.money_format;
    } else if (window.theme?.moneyFormat) {
      format = window.theme.moneyFormat;
    }
    
    const noDecimals = Math.round(cents / 100);
    
    return format
      .replace('{{amount}}', noDecimals)
      .replace('{{amount_no_decimals}}', noDecimals)
      .replace('{{amount_with_comma_separator}}', noDecimals)
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals)
      .replace('{{amount_with_space_separator}}', noDecimals)
      .replace('{{amount_no_decimals_with_space_separator}}', noDecimals);
  }

  observeNewContent() {
    const observer = new MutationObserver(() => {
      setTimeout(() => this.processBundles(), 100);
    });

    const main = document.querySelector('main') || document.body;
    observer.observe(main, { childList: true, subtree: true });
  }
}

// Initialize
new BundleBadgeManager(BUNDLE_CONFIG);






